import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { Container } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { MASTER_AE_PATHS } from "../../../../Routes/paths";
import { useAdverseEventApi } from "../api/useAdverseEventApi";
import { useAdverseEventFollowupApi } from "../api/useAdverseEventFollowupApi";
import { useAdverseEventMedicationsApi } from "../api/useAdverseEventMedicationsApi";
import { useAdverseEventAssessmentApi } from "../api/useAdverseEventAssessmentApi";
import {
  DateFormat,
  formatDate,
  showToastMessage,
  GetUserData,
} from "../../../../common/service/CommonService";
import { BreadcrumbContext } from "../../../../Layouts/Header";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { loadCodelistsDetailId, AE_DIAGNOSIS_OPTIONS } from "api/modules/common.option";
import { useGlobalStudy } from "context/StudyContext";
import { useVolunteerEnrollmentService } from "modules/Clinical/SubjectEnrollment/api/useVolunteerEnrollment";
import { queryClient } from "api/queryClient";
import AdverseEventEventDetailsForm from "../components/AdverseEventEventDetailsForm";
import AdverseEventFollowupsForm from "../components/AdverseEventFollowupsForm";
import AdverseEventMedicationsForm from "../components/AdverseEventMedicationsForm";
import AdverseEventPiAssessmentForm from "../components/AdverseEventPiAssessmentForm";
import FullPageLoader from "../../../../Components/Common/FullPageLoader";
import { mapFollowupsDataToForm, mapAdverseEventDataToForm, mapMedicationsDataToForm } from "../helper/adverseeventconstant";

type AETab = "event_details" | "followups" | "medications" | "pi_assessment";

const STEP_TO_TAB: Record<number, AETab> = {
  1: "event_details",
  2: "followups",
  3: "medications",
  4: "pi_assessment",
};

const TAB_TO_STEP: Record<AETab, number> = {
  event_details: 1,
  followups: 2,
  medications: 3,
  pi_assessment: 4,
};

const AddAdverseEvent: React.FC = () => {
  document.title = "Log New Adverse Event | ZENTIXS VISTA";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab") as AETab;
  const activeTab: AETab = ["event_details", "followups", "medications", "pi_assessment"].includes(tabParam)
    ? tabParam
    : "event_details";

  const currentStep = TAB_TO_STEP[activeTab];

  const [isTabChanging, setIsTabChanging] = useState(false);

  const setCurrentStep = (step: number) => {
    setIsTabChanging(true);
    const targetTab = STEP_TO_TAB[step] || "event_details";
    setSearchParams({ tab: targetTab }, { replace: true });
    setTimeout(() => {
      setIsTabChanging(false);
    }, 300);
  };
  const [eventDetailsData, setEventDetailsData] = useState<any>(null);
  const [followupData, setFollowupData] = useState<any>([]);
  const [medicationData, setMedicationData] = useState<any>([]);
  const [isEventDetailsSaved, setIsEventDetailsSaved] = useState(() => {
    return !!localStorage.getItem("ae_draft_id");
  });
  const [isFollowupsSaved, setIsFollowupsSaved] = useState(false);
  const [isMedicationsSaved, setIsMedicationsSaved] = useState(false);
  const [adverseEventId, setAdverseEventId] = useState<number | null>(() => {
    const savedId = localStorage.getItem("ae_draft_id");
    return savedId ? Number(savedId) : null;
  });

  const { InsertMutation, UpdateMutation, SelectByIdQuery } = useAdverseEventApi({
    adverseeventid: adverseEventId ? Number(adverseEventId) : null,
  });

  const isInitializingRef = useRef(false);
  const { SelectQuery: followupQuery, InsertMutation: InsertFollowupMutation, UpdateMutation: UpdateFollowupMutation } = useAdverseEventFollowupApi({
    adverseeventid: adverseEventId ? Number(adverseEventId) : null,
  });
  const { SelectQuery: assessmentQuery, UpdateMutation: UpdateAssessmentMutation } = useAdverseEventAssessmentApi({
    adverseeventid: adverseEventId ? Number(adverseEventId) : null,
    enabled: activeTab === "pi_assessment" && !!adverseEventId,
  });
  const [savingCardIndex, setSavingCardIndex] = useState<number | null>(null);
  const { selectedStudy: globalStudy } = useGlobalStudy();

  /* ── Codelists ── */
  const [codelistsLoading, setCodelistsLoading] = useState(true);
  useEffect(() => {
    setCodelistsLoading(true);
    loadCodelistsDetailId([
      "AE_STATUS_OPTIONS",
      "AE_SEVERITY_OPTIONS",
      "AE_CAUSALITY_OPTIONS",
      "AE_ACTION_TAKEN_OPTIONS",
      "AE_REPORT_METHOD_OPTIONS",
      "AE_OCCURRENCE_OPTIONS",
      "AE_OUTCOME_OPTIONS",
      "AE_LIKELIHOOD_OPTIONS",
      "AE_RELATIONSHIP_OPTIONS",
      "AE_PERIOD_OPTIONS",
      "AE_DIAGNOSIS_OPTIONS",
      "Routeof_Administration_OPTIONS",
    ]).finally(() => setCodelistsLoading(false));
  }, []);



  /* ── Study dropdown ── */
  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });
  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: Number(study.studyid),
        studycode: study.studycode,
      })) || [],
    [studySelectAllQuery.data],
  );

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const formattedToday = `${yyyy}-${mm}-${dd}`;

  const hh = String(today.getHours()).padStart(2, "0");
  const min = String(today.getMinutes()).padStart(2, "0");
  const formattedTime = `${hh}:${min}`;

  /* ── Form ── */
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
    trigger,
  } = useForm<any>({
    defaultValues: {
      studycode: "",
      subjectno: "",
      period: "",
      onsetdate: formattedToday,
      onsettime: formattedTime,
      reportingdate: formattedToday,
      reportingtime: formattedTime,
      recordingdate: formattedToday,
      recordingtime: formattedTime,
      onsetofevent: "",
      eventterm: "",
      severitygrade: "",
      reportmethod: "",
      occurrence: "",
      lastdosedate: formattedToday,
      lastdosetime: formattedTime,

      medicalexamstart: formattedTime,
      medicalexamend: formattedTime,
      bloodpressure: "",
      pulse: "",
      respiratoryrate: "",
      temperature: "",
      temperaturelocation: "",
      temperatureunit: "",
      associatedsymptoms: "",
      symptomsremark: "",
      generalexamination: "",
      systemicexamination: "",
      investigationadvised: "",
      examremark: "",
      diagnosis: "",
      diagnosisremarks: "",
      actiontaken: "",
      isfollowuprequired: "no",

      causality: "",
      otheraction: "",
      isadverse: "no",
      eventnarrative: "",
      followups: [
        {
          folloupdate: "",
          starttime: "",
          endtime: "",
          bloodpressure: "",
          pulse: null,
          respiratoryrate: null,
          temperature: null,
          temperaturelocation: "",
          temperatureunit: "",
          description: "",
          nextfollowuprequired: "",
        },
      ],
      medicines: [],
      piseverity: "",
      picausality: "",
      piactiontaken: "",
      outcomeofevent: "",
      likelihood: "",
      relationship: "",
      consumedmedication: "yes",
      reviewedby: "",
    },
    mode: "onChange",
  });

  const {
    fields: followupFields,
    append: appendFollowup,
    remove: removeFollowup,
    replace: replaceFollowups,
  } = useFieldArray({
    control,
    name: "followups",
  });


  useEffect(() => {
    const data = SelectByIdQuery.data?.data?.[0];
    if (!data) return;

    // Populate form from saved record — mark initializing to avoid
    isInitializingRef.current = true;
    const mappedFormValues = mapAdverseEventDataToForm(data, (d: string) => {
      const formatted = formatDate(d);
      return formatted === "-" ? "" : formatted;
    });

    Object.keys(mappedFormValues).forEach((key) => {
      setValue(key, (mappedFormValues as any)[key]);
    });

    // allow other effects to run after the next tick
    setTimeout(() => {
      isInitializingRef.current = false;
    }, 0);
  }, [SelectByIdQuery.data, setValue]);

  useEffect(() => {
    if (assessmentQuery.data?.data) {
      const rec = Array.isArray(assessmentQuery.data.data)
        ? assessmentQuery.data.data[0]
        : assessmentQuery.data.data;
      if (rec) {
        if (rec.severity) setValue("piseverity", Number(rec.severity));
        if (rec.actiontaken) setValue("piactiontaken", Number(rec.actiontaken));
        if (rec.causality) setValue("picausality", Number(rec.causality));
        if (rec.outcomeofevent) setValue("outcomeofevent", Number(rec.outcomeofevent));
        if (rec.likelihood) setValue("likelihood", Number(rec.likelihood));
        if (rec.relationship) setValue("relationship", Number(rec.relationship));

        const diagVal = getValues("diagnosis") || rec.diagnosis;
        const diagRemarks = getValues("diagnosisremarks") || rec.diagnosisremarks;
        const selectedOption = AE_DIAGNOSIS_OPTIONS.find(
          (opt) => Number(opt.value) === Number(diagVal)
        );
        const isFinalSelected =
          selectedOption && selectedOption.label.toLowerCase().includes("final");

        if (rec.finaldiagnosis) {
          setValue("finaldiagnosis", rec.finaldiagnosis);
        } else if (isFinalSelected && diagRemarks) {
          setValue("finaldiagnosis", diagRemarks);
        }
      }
    }
  }, [assessmentQuery.data, setValue, getValues]);

  useEffect(() => {
    if (activeTab === "pi_assessment") {
      const sevVal = getValues("severitygrade");
      const actVal = getValues("actiontaken");
      if (sevVal && !getValues("piseverity")) setValue("piseverity", Number(sevVal));
      if (actVal && !getValues("piactiontaken")) setValue("piactiontaken", Number(actVal));

      const diagVal = getValues("diagnosis");
      const diagRemarks = getValues("diagnosisremarks");
      const selectedOption = AE_DIAGNOSIS_OPTIONS.find(
        (opt) => Number(opt.value) === Number(diagVal)
      );
      const isFinalSelected =
        selectedOption && selectedOption.label.toLowerCase().includes("final");

      if (isFinalSelected && diagRemarks) {
        setValue("finaldiagnosis", diagRemarks);
      }
    }
  }, [activeTab, getValues, setValue]);

  useEffect(() => {
    if (followupQuery.data?.data && Array.isArray(followupQuery.data.data)) {
      const mappedFollowups = mapFollowupsDataToForm(followupQuery.data.data, (d: string) => {
        const formatted = formatDate(d);
        return formatted === "-" ? "" : formatted;
      });
      if (mappedFollowups.length > 0) {
        replaceFollowups(mappedFollowups.slice(0, 1));
      }
    }
  }, [followupQuery.data, replaceFollowups]);


  // Sync studycode with globalStudy
  useEffect(() => {
    // Skip syncing from globalStudy while initializing from a saved record
    if (isInitializingRef.current) return;

    if (globalStudy?.studyid && studyOptions.length > 0) {
      const matched = studyOptions.find(
        (s: any) => String(s.value) === String(globalStudy.studyid)
      );
      if (matched) {
        setValue("studycode", matched.value);
      } else {
        setValue("studycode", "");
      }
    } else {
      setValue("studycode", "");
    }
  }, [globalStudy?.studyid, studyOptions, setValue]);

  const selectedStudyId = watch("studycode");

  const onsetDateValue = watch("onsetdate");
  const onsetTimeValue = watch("onsettime");
  const reportingDateValue = watch("reportingdate");
  const reportingTimeValue = watch("reportingtime");
  const lastDoseDateValue = watch("lastdosedate");
  const lastDoseTimeValue = watch("lastdosetime");
  const medicalExamStartValue = watch("medicalexamstart");
  const medicalExamEndValue = watch("medicalexamend");
  const watchedAssociatedSymptoms = watch("associatedsymptoms");
  const diagnosisValue = watch("diagnosis");

  useEffect(() => {
    if (
      onsetDateValue ||
      onsetTimeValue ||
      reportingDateValue ||
      reportingTimeValue ||
      lastDoseDateValue ||
      lastDoseTimeValue ||
      medicalExamStartValue ||
      medicalExamEndValue
    ) {
      trigger("reportingdate");
      trigger("reportingtime");
      trigger("onsettime");
      trigger("lastdosedate");
      trigger("lastdosetime");
      trigger("medicalexamend");
    }
  }, [
    onsetDateValue,
    onsetTimeValue,
    reportingDateValue,
    reportingTimeValue,
    lastDoseDateValue,
    lastDoseTimeValue,
    medicalExamStartValue,
    medicalExamEndValue,
    trigger,
  ]);

  useEffect(() => {
    if (diagnosisValue) {
      const selectedOption = AE_DIAGNOSIS_OPTIONS.find(opt => Number(opt.value) === Number(diagnosisValue));
      const label = selectedOption?.label?.toLowerCase() || "";
      const isRemarkAllowed = label === "other" || label.includes("final");
      if (!isRemarkAllowed) {
        setValue("diagnosisremarks", "");
        clearErrors("diagnosisremarks");
      }
    }
  }, [diagnosisValue, setValue, clearErrors]);

  useEffect(() => {
    if (watchedAssociatedSymptoms === "no") {
      setValue("symptomsremark", "");
      clearErrors("symptomsremark");
    }
  }, [watchedAssociatedSymptoms, setValue, clearErrors]);

  /* ── Study details and Period options ── */
  const { SelectQuery: studyDetailQuery } = useMasterStudyService({
    id: selectedStudyId ? Number(selectedStudyId) : null,
    enabledSelect: !!selectedStudyId,
  });

  const periodOptions = useMemo(() => {
    const studyDetail = (studyDetailQuery.data as any)?.data?.[0];
    if (studyDetail?.periods && Array.isArray(studyDetail.periods)) {
      return studyDetail.periods.map((p: any) => {
        const num = p.period_number || p.period_no || p.periodorder || 1;
        const formattedNum = String(num).padStart(2, "0");
        return {
          label: formattedNum,
          value: formattedNum,
        };
      });
    }
    return [];
  }, [studyDetailQuery.data]);

  /* ── Subjects dropdown ── */
  const { SelectAllQuery: subjectsQuery } = useVolunteerEnrollmentService({
    studyid: selectedStudyId ? Number(selectedStudyId) : null,
  });

  const subjectsOptions = useMemo(() => {
    const list = (subjectsQuery?.data as any)?.data || [];

    return list
      .filter(
        (item: any) =>
          item?.subjectno &&
          item?.volunteerid &&
          item?.replacementstatus?.toLowerCase() !== "replaced" &&
          /^R*\d+$/.test(item.subjectno)
      )
      .map((item: any) => ({
        label: item.subjectno,
        value: item.subjectno,
        createddate: item.createddate,
      }));
  }, [subjectsQuery?.data]);



  /* ── Clear subject and period on study change ── */
  useEffect(() => {
    // Don't clear subject/period while we are initializing from saved data
    if (isInitializingRef.current) return;
    setValue("subjectno", "");
    setValue("period", "");
  }, [selectedStudyId, setValue]);

  /* ── Submit Handlers ── */
  const handleEventDetailsSubmit = (data: any) => {
    const selectedStudy = studyOptions.find(
      (s: any) => String(s.value) === String(data.studycode)
    );

    const user = GetUserData();

    const payload = {
      createdby: user?.userid || "-",

      studyid: data.studycode ? Number(data.studycode) : null,
      studycode: selectedStudy?.studycode || "",
      subjectno: data.subjectno,
      periodno: data.period || "",

      onsetdate: DateFormat(data.onsetdate) || null,
      onsettime: data.onsettime ? (data.onsettime.length === 5 ? `${data.onsettime}:00` : data.onsettime) : null,
      reportingdate: DateFormat(data.reportingdate) || null,
      reportingtime: data.reportingtime ? (data.reportingtime.length === 5 ? `${data.reportingtime}:00` : data.reportingtime) : null,
      recordingdate: DateFormat(data.recordingdate) || null,
      recordingtime: data.recordingtime ? (data.recordingtime.length === 5 ? `${data.recordingtime}:00` : data.recordingtime) : null,
      onsetofevent: data.onsetofevent === "predose" ? "Pre dose" : "Post dose",

      severity: data.severitygrade ? Number(data.severitygrade) : null,
      reportmethod: data.reportmethod ? Number(data.reportmethod) : null,
      occurrence: data.occurrence ? Number(data.occurrence) : null,
      lastdosereceiveddate: DateFormat(data.lastdosedate) || null,
      lastdosereceivedtime: data.lastdosetime ? (data.lastdosetime.length === 5 ? `${data.lastdosetime}:00` : data.lastdosetime) : null,
      description: data.eventterm || "",

      medicalexamstarttime: data.medicalexamstart ? (data.medicalexamstart.length === 5 ? `${data.medicalexamstart}:00` : data.medicalexamstart) : null,
      medicalexamendtime: data.medicalexamend ? (data.medicalexamend.length === 5 ? `${data.medicalexamend}:00` : data.medicalexamend) : null,
      bloodpressure: data.bloodpressure || "",
      pulse: data.pulse ? Number(data.pulse) : null,
      respiratoryrate: data.respiratoryrate ? Number(data.respiratoryrate) : null,
      temperature: data.temperature ? Number(data.temperature) : null,
      temperaturelocation: data.temperaturelocation || null,
      temperatureunit: data.temperatureunit || null,

      othersymptoms: data.associatedsymptoms === "yes",
      othersymptomsremark: data.symptomsremark || "",

      generalexamination: data.generalexamination === "normal" ? "Normal" : "Abnormal",
      systemicexamination: data.systemicexamination === "normal" ? "Normal" : "Abnormal",
      investigationadvised: data.investigationadvised || "",
      remarks: data.examremark || "",
      diagnosis: data.diagnosis ? Number(data.diagnosis) : null,
      diagnosisremarks: data.diagnosisremarks || "",
      actiontaken: data.actiontaken ? Number(data.actiontaken) : null,
      isfollowuprequired: data.isfollowuprequired === "yes",
    };

    InsertMutation.mutate(payload as any, {
      onSuccess: (res: any) => {
        showToastMessage("success", res?.message);
        queryClient.invalidateQueries({
          queryKey: ["verification-notification-count"],
        });
        setIsEventDetailsSaved(true);
        const newId = res?.data?.[0]?.adverseeventid || res?.data?.adverseeventid || res?.id;
        if (newId) {
          localStorage.setItem("ae_draft_id", String(newId));
        }
        setAdverseEventId(newId);
        setEventDetailsData(data);
      },
      onError: (err: any) => {
        showToastMessage(
          "error",
          err?.response?.data?.message || err?.data?.message || err?.message || err || "Failed to log Adverse Event"
        );
      },
    });
  };

  const handleSaveCard = async (index: number) => {
    if (!adverseEventId) {
      showToastMessage("error", "Please save Event Details (Step 1) before saving follow-ups.");
      return;
    }

    setSavingCardIndex(index);
    try {
      const user = GetUserData() as any;
      const getTzOffset = () => {
        const offsetMinutes = new Date().getTimezoneOffset();
        const absOffset = Math.abs(offsetMinutes);
        const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
        const minutes = String(absOffset % 60).padStart(2, "0");
        const sign = offsetMinutes <= 0 ? "+" : "-";
        return `${sign}${hours}:${minutes}`;
      };
      const offset = getTzOffset();

      const fu = getValues(`followups.${index}`);
      if (!fu) return;

      const timePart = fu.starttime ? (fu.starttime.length === 5 ? `${fu.starttime}:00` : fu.starttime) : "00:00:00";
      const followupPayload = {
        followupdate: fu.folloupdate ? `${fu.folloupdate}T${timePart}${offset}` : null,
        starttime: fu.starttime ? `${fu.starttime.length === 5 ? `${fu.starttime}:00` : fu.starttime}${offset}` : null,
        endtime: fu.endtime ? `${fu.endtime.length === 5 ? `${fu.endtime}:00` : fu.endtime}${offset}` : null,
        bloodpressure: fu.bloodpressure || "",
        pulse: fu.pulse ? Number(fu.pulse) : null,
        respiratoryrate: fu.respiratoryrate ? Number(fu.respiratoryrate) : null,
        temperature: fu.temperature ? Number(fu.temperature) : null,
        temperaturelocation: fu.temperaturelocation || null,
        temperatureunit: fu.temperatureunit || "",
        description: fu.description || "",
        nextfollowuprequired: fu.nextfollowuprequired === "yes",
      };

      let res: any;
      if (fu.followupid) {
        res = await UpdateFollowupMutation.mutateAsync({
          adverseeventid: adverseEventId,
          updatereason: "",
          followup: {
            followupid: Number(fu.followupid),
            ...followupPayload,
          },
        });
      } else {
        res = await InsertFollowupMutation.mutateAsync({
          adverseeventid: adverseEventId,
          followup: followupPayload,
        });
        const newFollowupId =
          res?.data?.followupid ||
          res?.data?.[0]?.followupid ||
          res?.followupids?.[0] ||
          res?.data?.followupids?.[0] ||
          res?.data?._followupids?.[0] ||
          res?.id;
        if (newFollowupId) {
          setValue(`followups.${index}.followupid`, newFollowupId);
        }
      }

      setValue(`followups.${index}.isSaved`, true);
      showToastMessage("success", res?.message || "Follow-up details saved successfully");
    } catch (err: any) {
      showToastMessage("error", err?.response?.data?.message || err?.data?.message || err?.message || err || "Failed to save follow-up");
    } finally {
      setSavingCardIndex(null);
    }
  };

  const { InsertMutation: InsertMedicationsMutation, SelectQuery: medicationsQuery } = useAdverseEventMedicationsApi({
    adverseeventid: adverseEventId ? Number(adverseEventId) : null,
    enabled: !!adverseEventId,
  });

  useEffect(() => {
    if (medicationsQuery.data?.data) {
      const mapped = mapMedicationsDataToForm(medicationsQuery.data.data, (d: string) => {
        const formatted = formatDate(d);
        return formatted === "-" ? "" : formatted;
      });

      // Populate medication-related fields
      setValue("resolutiondate", mapped.resolutiondate || "");
      setValue("resolutiontime", mapped.resolutiontime || "");
      setValue("consumedmedication", mapped.consumedmedication || "");
      setValue("medicines", mapped.medicines || []);

      // disabled on refresh
      if (Array.isArray(mapped.medicines) && mapped.medicines.length > 0) {
        setIsMedicationsSaved(true);
      }
    }
  }, [medicationsQuery.data, setValue]);

  const handleMedicationsNext = async (data: any) => {
    if (!adverseEventId) {
      showToastMessage("error", "Please save Event Details first.");
      return;
    }

    try {
      const getTzOffset = () => {
        const offsetMinutes = new Date().getTimezoneOffset();
        const absOffset = Math.abs(offsetMinutes);
        const hours = String(Math.floor(absOffset / 60)).padStart(2, "0");
        const minutes = String(absOffset % 60).padStart(2, "0");
        const sign = offsetMinutes <= 0 ? "+" : "-";
        return `${sign}${hours}:${minutes}`;
      };
      const offset = getTzOffset();

      const medicinesPayload = (data.medicines || []).map((med: any) => {
        return {
          brandname: med.brandname || "",
          genericname: med.genericname || "",
          frequency: med.frequency || "",
          route: med.route ? Number(med.route) : null,
          startdate: med.startdate ? `${med.startdate}T00:00:00${offset}` : null,
          enddate: med.enddate ? `${med.enddate}T00:00:00${offset}` : null,
          interactionwithip: med.interaction || med.interactionwithip || "",
        };
      });

      const payload = {
        adverseeventid: adverseEventId,
        resolutiondate: data.resolutiondate ? `${data.resolutiondate}T${data.resolutiontime || "00:00"}:00${offset}` : null,
        resolutiontime: data.resolutiontime ? `${data.resolutiontime}${offset}` : null,
        medicineconsumed: data.consumedmedication === "yes" ? "Yes" : (data.consumedmedication === "no" ? "No" : "NA"),
        medicines: medicinesPayload,
      };

      const res: any = await InsertMedicationsMutation.mutateAsync(payload as any);
      showToastMessage("success", res?.message || "Adverse Event Medications record inserted successfully!");
      setIsMedicationsSaved(true);
    } catch (err: any) {
      showToastMessage(
        "error",
        err?.response?.data?.message || err?.data?.message || err?.message || err || "Failed to save medications"
      );
    }
  };

  const handlePiAssessmentSubmit = async (data: any) => {
    if (!adverseEventId) {
      showToastMessage("error", "Adverse Event ID is missing");
      return;
    }

    try {
      const assessmentPayload = {
        adverseeventid: adverseEventId,
        updatereason: "PI Assessment submitted",
        assessment: {
          finaldiagnosis: data.finaldiagnosis || "",
          causality: data.picausality ? Number(data.picausality) : null,
          outcomeofevent: data.outcomeofevent ? Number(data.outcomeofevent) : null,
          likelihood: data.likelihood ? Number(data.likelihood) : null,
          relationship: data.relationship ? Number(data.relationship) : null,
        },
      };

      const res: any = await UpdateAssessmentMutation.mutateAsync(assessmentPayload);
      localStorage.removeItem("ae_draft_id");
      showToastMessage("success", res?.message || "Adverse Event Assessment saved successfully");
      navigate(MASTER_AE_PATHS.LIST);
    } catch (err: any) {
      showToastMessage(
        "error",
        err?.response?.data?.message || err?.data?.message || err?.message || err || "Failed to submit Adverse Event Assessment"
      );
    }
  };

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Adverse Event", link: MASTER_AE_PATHS.LIST },
      { text: "Log New Adverse Event" },
    ]);

    return () => {
      // Clear draft ID when user leaves Log New Adverse Event page
      localStorage.removeItem("ae_draft_id");
    };
  }, []);

  const watchedIsFollowupRequired = watch("isfollowuprequired");

  if (studySelectAllQuery.isLoading || codelistsLoading) {
    return (
      <div className="page-content" style={{ backgroundColor: "#f4f6fc", minHeight: "100vh" }}>
        <FullPageLoader message="Loading form details..." />
      </div>
    );
  }

  return (
    <div
      className="page-content"
      style={{
        backgroundColor: "#f4f6fc",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {(InsertMutation.isPending || UpdateMutation.isPending || followupQuery.isFetching || isTabChanging) && (
        <FullPageLoader
          message={
            InsertMutation.isPending || UpdateMutation.isPending
              ? "Saving Adverse Event..."
              : "Loading details..."
          }
        />
      )}
      <Container fluid className="d-flex flex-column flex-grow-1">
        {currentStep === 1 && (
          <AdverseEventEventDetailsForm
            control={control}
            register={register}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            clearErrors={clearErrors}
            studyOptions={studyOptions}
            subjectsOptions={subjectsOptions}
            periodOptions={periodOptions}
            isSubjectLoading={subjectsQuery.isLoading}
            onSubmit={handleSubmit(handleEventDetailsSubmit)}
            isPending={InsertMutation.isPending}
            onCancel={() => navigate(MASTER_AE_PATHS.DASHBOARD)}
            isSaved={isEventDetailsSaved}
            onNext={() => setCurrentStep(2)}
          />
        )}
        {currentStep === 2 && (
          <AdverseEventFollowupsForm
            control={control}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            clearErrors={clearErrors}
            trigger={trigger}
            onSubmit={(e: any) => e.preventDefault()}
            onBack={() => setCurrentStep(1)}
            onCancel={() => navigate(MASTER_AE_PATHS.DASHBOARD)}
            isSaved={isFollowupsSaved}
            onNext={() => setCurrentStep(3)}
            onSaveCard={handleSaveCard}
            savingCardIndex={savingCardIndex}
            isFollowupRequired={watchedIsFollowupRequired}
            fields={followupFields}
            append={appendFollowup}
            remove={removeFollowup}
          />
        )}
        {currentStep === 3 && (
          <AdverseEventMedicationsForm
            control={control}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            clearErrors={clearErrors}
            onSubmit={handleSubmit(handleMedicationsNext)}
            onBack={() => setCurrentStep(2)}
            onNext={() => setCurrentStep(4)}
            onCancel={() => navigate(MASTER_AE_PATHS.DASHBOARD)}
            isPending={InsertMedicationsMutation.isPending}
            isSaved={isMedicationsSaved}
          />
        )}
        {currentStep === 4 && (
          <AdverseEventPiAssessmentForm
            control={control}
            setValue={setValue}
            watch={watch}
            getValues={getValues}
            errors={errors}
            clearErrors={clearErrors}
            onSubmit={handleSubmit(handlePiAssessmentSubmit)}
            onBack={() => setCurrentStep(3)}
            onCancel={() => navigate(MASTER_AE_PATHS.DASHBOARD)}
            isPending={UpdateAssessmentMutation.isPending}
          />
        )}
      </Container>
    </div>
  );
};

export default AddAdverseEvent;
